import React, { useState, useRef } from 'react';
import { CustomInput, CustomTextarea, CustomToggle } from './CustomFormElements';
import Button from './Buttons';
import ThemedSearchInput from './SearchInput';

export interface EditableItem {
  id?: string | number;
  _tempId?: string | number;
  keyword: string;
  is_active?: boolean;
  definition: string;
}

export interface EditableItemListProps {
  title?: string;
  description?: string;
  itemName?: string;
  viewAllText?: string;

  keywordLabel?: string;
  definitionLabel?: string;

  items: EditableItem[];
  totalItems: number;
  hasMoreItems?: boolean;
  isDisabled?: boolean;

  showActiveToggle?: boolean;
  showSearch?: boolean;

  updateItem: (itemId: string | number, field: keyof EditableItem, value: any) => void;
  addItem: () => void;
  removeItem: (itemId: string | number) => void;
  onViewAllClick?: () => void;
}

const EditableItemList: React.FC<EditableItemListProps> = ({
  description = 'Define the items below',
  itemName = 'Item',
  keywordLabel = 'Keyword',
  definitionLabel = 'Definition',
  items,
  totalItems,
  hasMoreItems = false,
  isDisabled = false,
  showActiveToggle = true,
  updateItem,
  addItem,
  removeItem,
  onViewAllClick,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleAddClick = () => {
    addItem();
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const filteredItems = items.filter((item) =>
    item.keyword?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
      <div className="d-flex justify-between align-center mb-2">
        <div>
          <h4 className="h3-title">{itemName} List</h4>
          <div className="small-font">
            {description}
            {hasMoreItems && (
              <span className="text-muted">
                {' '}
                (showing {filteredItems.length} of {totalItems} {itemName.toLowerCase()}s)
              </span>
            )}
          </div>
        </div>

        <div style={{ width: '250px' }}>
          <ThemedSearchInput
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder={`Search by ${itemName.toLowerCase()}...`}
          />
        </div>
      </div>

      {filteredItems.length === 0 && items.length > 0 && (
        <p className="text-muted small-font mt-2 mb-2">
          No {itemName.toLowerCase()}s found matching "{searchTerm}".
        </p>
      )}

      <div
        ref={scrollContainerRef}
        style={{
          maxHeight: filteredItems.length > 3 ? '800px' : 'auto',
          overflowY: filteredItems.length > 3 ? 'auto' : 'visible',
          paddingRight: filteredItems.length > 3 ? '8px' : '0',
        }}
      >
        {filteredItems.map((item) => {
          const currentId = item.id || item._tempId;

          if (!currentId) return null;

          return (
            <div key={currentId} className="attribute-row mb-2 p-2 border-yes radius-yes">
              <div className="d-flex flex-row gap align-center mb-1">
                <div className="flex-grow-1">
                  <CustomInput
                    id={`item-keyword-${currentId}`}
                    label={keywordLabel}
                    placeholder={`Enter ${keywordLabel.toLowerCase()}`}
                    type="text"
                    value={item.keyword}
                    onChange={(id: string, value: string) =>
                      updateItem(currentId, 'keyword', value)
                    }
                    disabled={isDisabled}
                  />
                </div>

                {showActiveToggle && (
                  <div className="align-wrapper d-flex align-center">
                    <CustomToggle
                      id={`item-active-${currentId}`}
                      label={item.is_active ? 'Active' : 'Inactive'}
                      checked={!!item.is_active}
                      onChange={(id: string, value: boolean) =>
                        updateItem(currentId, 'is_active', value)
                      }
                      disabled={isDisabled}
                    />
                  </div>
                )}

                {!isDisabled && (
                  <Button variant="danger" size="small" onClick={() => removeItem(currentId)}>
                    <i className="fa-solid fa-trash"></i>
                  </Button>
                )}
              </div>
              <div>
                <CustomTextarea
                  id={`item-definition-${currentId}`}
                  label={definitionLabel}
                  name={`definition-${currentId}`}
                  addedText={item.definition}
                  onChange={(value: string) => updateItem(currentId, 'definition', value)}
                  resizable={false}
                  disabled={isDisabled}
                  height="80px"
                />
              </div>
            </div>
          );
        })}
      </div>

      {hasMoreItems && onViewAllClick && (
        <div className="mt-2 d-flex justify-center">
          <Button variant="secondary" size="small" onClick={onViewAllClick}>
            View All
          </Button>
        </div>
      )}

      {!isDisabled && (
        <Button variant="secondary" size="medium" onClick={handleAddClick} className="mt-2">
          <i className="fa-solid fa-plus"></i> Add {itemName}
        </Button>
      )}
    </div>
  );
};

export default EditableItemList;
